import { Button, Tag, Tooltip, notification } from "antd";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { IProject } from "projects/models";
import { projectService } from "projects/services";
import { useBoundStore } from "store";
import { projectKeys } from "projects/hooks";
import { getSharePointFolderStatusMeta } from "projects/utils/sharePointFolderStatus";

type Props = { project: IProject };

/**
 * Shows whether a project is "connected" to a SharePoint folder, and — when the
 * folder is missing — offers a one-click "Recreate folder" action.
 */
export const SharePointFolderStatus = ({ project }: Props) => {
  const { tenant, currentOrganization } = useBoundStore();
  const queryClient = useQueryClient();
  const [api, contextHolder] = notification.useNotification();
  const [loading, setLoading] = useState(false);

  const meta = getSharePointFolderStatusMeta(project.sharePointFolderStatus);

  const tag =
    project.sharePointFolderStatus === "created" && project.sharePointFolderUrl ? (
      <Tag color={meta.color}>
        <a href={project.sharePointFolderUrl} target="_blank" rel="noreferrer">
          {meta.label}
        </a>
      </Tag>
    ) : (
      <Tooltip title={project.sharePointFolderError ?? undefined}>
        <Tag color={meta.color}>{meta.label}</Tag>
      </Tooltip>
    );

  const canRecreate =
    meta.isMissing && Boolean(tenant?.id && currentOrganization?.id && project.id);

  const handleRecreate = async () => {
    if (!tenant?.id || !currentOrganization?.id || !project.id) return;
    setLoading(true);
    try {
      const result = await projectService.recreateSharePointFolder(
        tenant.id,
        currentOrganization.id,
        project.id
      );
      if (result?.status === "created") {
        api.success({ message: "SharePoint folder created", duration: 3 });
      } else {
        api.warning({
          message: "SharePoint folder not created",
          description: result?.warning,
          duration: 0,
        });
      }
      await queryClient.invalidateQueries({
        queryKey: projectKeys.detail(tenant.id, currentOrganization.id, project.id),
      });
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
    } catch (error: any) {
      api.error({ message: "Could not recreate folder", description: error?.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      {tag}
      {canRecreate && (
        <Button size="small" onClick={handleRecreate} loading={loading}>
          Recreate folder
        </Button>
      )}
    </>
  );
};

export default SharePointFolderStatus;
